/// OBS error-monitoring — báo lỗi runtime phía Web về server (best-effort).
/// Ref: Frontend DD §2.3, SRS FR-003 / NFR-REL-001/002.
///
/// NGUYÊN TẮC: fail-silent. Lỗi khi gửi bị nuốt, KHÔNG hiển thị cho người dùng,
/// KHÔNG report lại lỗi HTTP do API mình trả về (backend đã thu thập → tránh trùng).
import axios from 'axios'

import { errorMonitoringApi } from '@/features/error-monitoring/api/error-monitoring-api'

let reporting = false
const recent = new Map<string, number>()
const THROTTLE_MS = 10_000

function appVersion(): string | undefined {
  return (import.meta.env.VITE_APP_VERSION as string | undefined) || undefined
}

/** Lỗi HTTP từ API mình (axios) → bỏ qua (backend đã thu thập). */
function isApiError(err: unknown): boolean {
  return axios.isAxiosError(err)
}

// Các lỗi vô hại / do thư viện UI ném ra nhưng KHÔNG ảnh hưởng người dùng →
// không đẩy về backend (tránh rác màn Giám sát Lỗi). Khớp theo message.
const IGNORED_MESSAGE_PATTERNS: string[] = []

function isIgnoredError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '')
  return IGNORED_MESSAGE_PATTERNS.some((p) => msg.includes(p))
}

function currentRoute(): string | undefined {
  if (typeof window === 'undefined') return undefined
  return window.location.hash || window.location.pathname
}

export function reportClientError(
  error: unknown,
  info?: { route?: string; context?: Record<string, unknown> },
): void {
  try {
    if (reporting || isApiError(error) || isIgnoredError(error)) return
    const e = error instanceof Error ? error : new Error(String(error))
    const key = `${e.name}:${(e.message || '').slice(0, 80)}`
    const now = Date.now()
    if (now - (recent.get(key) ?? 0) < THROTTLE_MS) return
    if (recent.size > 200) recent.clear()
    recent.set(key, now)

    reporting = true
    void errorMonitoringApi
      .reportClientError({
        layer: 'web',
        severity: 'error',
        errorType: e.name || 'Error',
        message: e.message || String(error),
        stacktrace: e.stack,
        appVersion: appVersion(),
        platform: 'web',
        route: info?.route ?? currentRoute(),
        context: info?.context,
      })
      .catch(() => {
        /* swallow — fail-silent */
      })
      .finally(() => {
        reporting = false
      })
  } catch {
    reporting = false
  }
}

/** Đăng ký global handlers (gọi 1 lần ở main.ts). */
export function registerGlobalErrorReporting(): void {
  if (typeof window === 'undefined') return
  window.addEventListener('error', (ev) => reportClientError(ev.error ?? ev.message))
  window.addEventListener('unhandledrejection', (ev) => reportClientError((ev as PromiseRejectionEvent).reason))
}
